import React, { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { BuilderState } from "./state";
import { VarDef } from "./emailVars";
import { EmailCanvasGlobalStyle } from "./builderStyled";
import { renderBlock, RenderCtx } from "./blockRenderers";
import { BlockShell } from "./BlockShell";
import { EmailBuilderActions, Mode } from "./useEmailBuilder";
import { useBlockDrag } from "./useBlockDrag";
import { useVarAutocomplete } from "./useVarAutocomplete";
import { VarAutocompleteMenu } from "./VarAutocompleteMenu";
import { buildChipHtml } from "./chipDom";
import { writeBackFromEl } from "./writeBack";
import {
	MIME_BLOCK,
	MIME_VAR,
	MIME_SECTION,
	computeDropBeforeId,
	readBlockPayload,
	readVarPayload,
	insertVarChipAtPoint,
	isSectionDrag,
	computeSectionDrop,
	SectionDropTarget,
} from "./paletteDnd";

const FONTS: Record<string, string> = {
	system: "-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
	arial: "Arial,Helvetica,sans-serif",
	georgia: "Georgia,'Times New Roman',serif",
	verdana: "Verdana,Geneva,sans-serif",
	tahoma: "Tahoma,Segoe UI,sans-serif",
};

/** Darkens a hex color by factor f (0–1). */
function shade(hex: string, f: number): string {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
	if (!m) return hex;
	const n = parseInt(m[1], 16);
	let r = (n >> 16) & 255;
	let g = (n >> 8) & 255;
	let b = n & 255;
	r = Math.round(r * (1 - f));
	g = Math.round(g * (1 - f));
	b = Math.round(b * (1 - f));
	return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function lum(hex: string): number {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
	if (!m) return 0;
	const n = parseInt(m[1], 16);
	const r = ((n >> 16) & 255) / 255;
	const g = ((n >> 8) & 255) / 255;
	const bv = (n & 255) / 255;
	const f = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
	return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(bv);
}

function ctext(hex: string): string {
	return lum(hex) > 0.55 ? "#1f2937" : "#ffffff";
}

export interface CanvasProps {
	state: BuilderState;
	sampleMode: boolean;
	darkMode: boolean;
	catalog: VarDef[];
	/** Present only in edit mode. */
	mode?: Mode;
	actions?: EmailBuilderActions;
}

/**
 * Email canvas — edit or preview.
 * Edit mode: each block wrapped in BlockShell (selection, drag, editable fields, row controls).
 * Preview mode: read-only renderBlock, no interactivity.
 */
export function Canvas({ state, sampleMode, darkMode, catalog, mode, actions }: CanvasProps): JSX.Element {
	const { brand, radius, font } = state.theme;
	const brandD = shade(brand, 0.38);
	const headerFg = ctext(brand);
	const fontStack = FONTS[font] ?? FONTS.system;
	const isEdit = mode === "edit" && !!actions;

	const ctx: RenderCtx = {
		elStyle: state.elStyle,
		theme: state.theme,
		sampleMode,
		catalog,
	};

	const cssVars = {
		"--brand": brand,
		"--brand-d": brandD,
		"--header-fg": headerFg,
		"--font": fontStack,
		"--radius": radius + "px",
	} as React.CSSProperties;

	const emailClass = "email" + (darkMode ? " dark" : "") + (isEdit ? " editing" : "");

	// ---- Drag reorder (edit mode only) ----
	const emailRef = useRef<HTMLDivElement>(null);
	const getBlockEls = useCallback((): NodeListOf<Element> | Element[] => {
		if (!emailRef.current) return [];
		return emailRef.current.querySelectorAll("[data-id]");
	}, []);

	const drag = useBlockDrag(
		useCallback(
			(fromId: string, beforeId: string | null) => {
				if (actions) actions.moveBlock(fromId, beforeId);
			},
			[actions]
		),
		getBlockEls
	);

	// ---- Palette drag-drop (drag a block/var FROM the palette INTO the canvas) ----
	// undefined = no palette block-drag active; otherwise the id to drop before (null = end).
	const [paletteDropBeforeId, setPaletteDropBeforeId] = useState<string | null | undefined>(undefined);
	// Section-title drop target (drag "Tiêu đề mục" from palette INTO a summary block).
	const [sectionDrop, setSectionDrop] = useState<SectionDropTarget | null>(null);
	// The text field currently highlighted as a var-drop target (transient class, not state).
	const varTargetRef = useRef<HTMLElement | null>(null);

	// Only summary blocks accept section titles — validate the resolved drop target.
	const isSummaryId = useCallback(
		(id: string | null) => !!id && state.blocks.some((b) => b.id === id && b.type === "summary"),
		[state.blocks]
	);

	const clearVarTarget = useCallback(() => {
		if (varTargetRef.current) {
			varTargetRef.current.classList.remove("wl-drop-target");
			varTargetRef.current = null;
		}
	}, []);

	const highlightVarTarget = useCallback((x: number, y: number) => {
		let el: HTMLElement | null = document.elementFromPoint(x, y) as HTMLElement | null;
		while (el && !el.isContentEditable) el = el.parentElement;
		if (el === varTargetRef.current) return;
		if (varTargetRef.current) varTargetRef.current.classList.remove("wl-drop-target");
		varTargetRef.current = el;
		if (el) el.classList.add("wl-drop-target");
	}, []);

	const onCanvasDragOver = useCallback(
		(e: React.DragEvent) => {
			if (!isEdit) return;
			const types = e.dataTransfer.types;
			const isBlock = types.includes(MIME_BLOCK);
			const isVar = types.includes(MIME_VAR);
			const isSection = types.includes(MIME_SECTION);
			if (!isBlock && !isVar && !isSection) return; // not a palette drag — ignore
			e.preventDefault(); // allow drop
			e.dataTransfer.dropEffect = "copy";
			if (isSection) {
				// Resolve the summary block + row index under the cursor; show the line
				// only when over a summary (titles can't live in other block types).
				const drop = computeSectionDrop(e.clientX, e.clientY, emailRef.current);
				setSectionDrop(drop && isSummaryId(drop.blockId) ? drop : null);
			} else if (isBlock) {
				setPaletteDropBeforeId(computeDropBeforeId(e.clientY, getBlockEls()));
			} else if (isVar) {
				highlightVarTarget(e.clientX, e.clientY);
			}
		},
		[isEdit, getBlockEls, highlightVarTarget, isSummaryId]
	);

	const onCanvasDragLeave = useCallback(
		(e: React.DragEvent) => {
			// Only clear when the cursor actually left the canvas (not a child boundary).
			if (e.currentTarget === e.target) {
				setPaletteDropBeforeId(undefined);
				setSectionDrop(null);
				clearVarTarget();
			}
		},
		[clearVarTarget]
	);

	const onCanvasDrop = useCallback(
		(e: React.DragEvent) => {
			if (!isEdit || !actions) return;
			const blockType = readBlockPayload(e.dataTransfer);
			const varPayload = readVarPayload(e.dataTransfer);
			const isSection = isSectionDrag(e.dataTransfer);
			if (!blockType && !varPayload && !isSection) return; // not a palette payload
			e.preventDefault();
			const target = varTargetRef.current || (e.target as EventTarget);
			clearVarTarget();
			if (isSection) {
				// Re-resolve at the drop point (drop fires without a final dragover).
				const drop = computeSectionDrop(e.clientX, e.clientY, emailRef.current);
				if (drop && isSummaryId(drop.blockId)) {
					actions.addSectionAt(drop.blockId, drop.index);
				}
			} else if (varPayload) {
				insertVarChipAtPoint(e.clientX, e.clientY, target, varPayload.name, varPayload.label, actions);
			} else if (blockType) {
				actions.addBlockAt(blockType, paletteDropBeforeId ?? null);
			}
			setPaletteDropBeforeId(undefined);
			setSectionDrop(null);
		},
		[isEdit, actions, paletteDropBeforeId, clearVarTarget, isSummaryId]
	);

	// ---- Inline ${ autocomplete (edit mode only) ----
	const insertVarAtRange = useCallback(
		(varName: string, label: string, deleteRange: Range) => {
			if (!actions) return;
			// identify the host element before we mutate the DOM
			const hostEl = (deleteRange.startContainer as Text).parentElement?.closest(
				"[data-el]"
			) as HTMLElement | null;
			// delete the typed "${partial" text
			deleteRange.deleteContents();
			// insert chip + trailing space via execCommand so the browser handles undo/redo
			const chipHtml = buildChipHtml(varName, label, false) + " ";
			try {
				document.execCommand("insertHTML", false, chipHtml);
			} catch (_) {
				// execCommand deprecated but still most reliable for contentEditable
			}
			// sync state via writeBack on the host editable
			if (hostEl) {
				const blockId = hostEl.getAttribute("data-el")?.split(":")[0] ?? null;
				if (blockId) writeBackFromEl(hostEl, blockId, actions);
			}
		},
		[actions]
	);

	const {
		menu,
		onKeyDown: acOnKeyDown,
		pick,
		close,
	} = useVarAutocomplete(emailRef, isEdit ? catalog : [], insertVarAtRange);

	return (
		<>
			<EmailCanvasGlobalStyle />
			<div
				ref={emailRef}
				className={emailClass}
				style={{ ...cssVars, position: "relative" }}
				onKeyDown={isEdit ? (e) => acOnKeyDown(e) : undefined}
				onDragOver={isEdit ? onCanvasDragOver : undefined}
				onDragLeave={isEdit ? onCanvasDragLeave : undefined}
				onDrop={isEdit ? onCanvasDrop : undefined}>
				{/* Section-title drop indicator — a brand line where the "# Title" will land */}
				{isEdit && sectionDrop && (
					<div
						aria-hidden
						style={{
							position: "absolute",
							left: 16,
							right: 16,
							top: sectionDrop.top - 1,
							height: 3,
							background: "var(--brand, #005e9e)",
							borderRadius: 3,
							zIndex: 9,
							pointerEvents: "none",
						}}
					/>
				)}
				{isEdit
					? state.blocks.map((block) => (
							<React.Fragment key={block.id}>
								{paletteDropBeforeId === block.id && <div className="palette-drop-line" />}
								<BlockShell
									block={block}
									actions={actions!}
									ctx={ctx}
									selId={state.selId}
									selEl={state.selEl}
									drag={drag}
								/>
							</React.Fragment>
					  ))
					: state.blocks.map((block) => (
							<div
								key={block.id}
								className="blk"
								data-id={block.id}>
								{renderBlock(block, ctx)}
							</div>
					  ))}
			</div>
			{isEdit &&
				ReactDOM.createPortal(
					<VarAutocompleteMenu
						menu={menu}
						pick={pick}
						close={close}
					/>,
					document.body
				)}
		</>
	);
}
